import type { ContactPresente } from '../../api/contacts.js';
import { RechercheContact } from '../ventes/RechercheContact.js';
import { Button, Dialog, Input, Select, PriceInput } from '@artosera/ui';
import { ImagePlus, X } from 'lucide-react';
import type { JSX } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { estApiError } from '../../api/errors.js';
import { useCreerArtiste, useArtistes } from '../../api/artistes.js';
import { useDeposerMedia } from '../../api/medias.js';
import { useCreerOeuvre, useNumeroInventaireExpected } from '../../api/oeuvres.js';
import { Field } from '../Field.js';
import { ChampsManquants } from '../ChampsManquants.js';
import { useErreurFormulaire } from '../useErreurFormulaire.js';
import { useFermetureGardee } from '../useFermetureGardee.js';
import { LIMITES, erreurAnnee } from '../validations.js';
import styles from './NouvelleOeuvreDialog.module.css';

export interface NouvelleOeuvreDialogProps {
  galerieSlug: string;
  registrePoliceActif: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreee: (id: string) => void;
}

const NOUVEL_ARTISTE = '__nouveau__';

/** Photos retenues à la création : au-delà, la fiche prend le relais. */
const PHOTOS_MAX = 10;

/**
 * Dialogue de création (3.3, écran 1) : photos, numéro d'inventaire proposé
 * par `GET /oeuvres/numero-inventaire` et modifiable, artiste avec création
 * rapide, erreurs de doublon affichées mot pour mot (2.2).
 *
 * Les photos restent dans le navigateur jusqu'à la création, puis partent une
 * à une, rattachées à la fiche (ARTO-202) : annuler n'envoie rien, et un envoi
 * qui échoue laisse une fiche à compléter, jamais un média sans œuvre.
 */
export function NouvelleOeuvreDialog({
  galerieSlug,
  registrePoliceActif,
  open,
  onOpenChange,
  onCreee,
}: NouvelleOeuvreDialogProps): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'commun']);

  const [vendeur, setVendeur] = useState<ContactPresente | null>(null);
  const [prixAchat, setPrixAchat] = useState<number | null>(null);
  const [titre, setTitre] = useState('');
  const [artisteId, setArtisteId] = useState('');
  const [nouvelArtisteNom, setNouvelArtisteNom] = useState('');
  const [numeroSaisi, setNumeroSaisi] = useState('');
  const [numeroTouche, setNumeroTouche] = useState(false);
  const [annee, setAnnee] = useState('');
  const [photos, setPhotos] = useState<readonly File[]>([]);
  const fichierRef = useRef<HTMLInputElement>(null);
  const formulaire = useErreurFormulaire();
  const [enCours, setEnCours] = useState(false);
  const [envoi, setEnvoi] = useState<{ rang: number; total: number } | null>(null);
  /** La fiche créée quand une photo n'est pas partie : on le dit avant de l'ouvrir. */
  const [creeeAvecProblemes, setCreeeAvecProblemes] = useState<string | null>(null);
  const [photosEnEchec, setPhotosEnEchec] = useState(0);

  const artistes = useArtistes(galerieSlug, { tri: 'nom', parPage: 200 });
  const propose = useNumeroInventaireExpected(
    galerieSlug,
    artisteId && artisteId !== NOUVEL_ARTISTE ? artisteId : undefined,
  );
  const creerArtiste = useCreerArtiste(galerieSlug);
  const creerOeuvre = useCreerOeuvre(galerieSlug);
  const deposerMedia = useDeposerMedia(galerieSlug);

  const apercus = useMemo(() => photos.map((photo) => URL.createObjectURL(photo)), [photos]);
  useEffect(
    () => () => {
      for (const url of apercus) URL.revokeObjectURL(url);
    },
    [apercus],
  );

  // Tant que la personne n'a pas touché le champ, il suit le numéro proposé
  // par l'API (valeur dérivée au rendu, pas d'effet — le parent remonte ce
  // composant par `key` à chaque ouverture, § « réinitialiser le formulaire »).
  const numero = numeroTouche ? numeroSaisi : (propose.data ?? '');

  const optionsArtistes = [
    { value: '', label: t('regie:oeuvres.nouvelleOeuvre.artisteChoisir'), disabled: true },
    ...(artistes.data?.artistes.map((artiste) => ({ value: artiste.id, label: artiste.nom })) ??
      []),
    { value: NOUVEL_ARTISTE, label: `+ ${t('regie:oeuvres.nouvelleOeuvre.artisteNouveau')}` },
  ];

  async function soumettre(): Promise<void> {
    formulaire.reinitialiser();
    setEnCours(true);
    try {
      let idArtiste = artisteId;
      if (artisteId === NOUVEL_ARTISTE) {
        const cree = await creerArtiste.mutateAsync({ nom: nouvelArtisteNom });
        idArtiste = cree.artiste.id;
      }
      // Un numéro laissé tel que proposé n'est pas envoyé : l'API réserve le
      // suivant libre, et deux créations simultanées n'ont jamais le même.
      const numeroSaisiPropre = numero.trim();
      const numeroEnvoye =
        numeroTouche && numeroSaisiPropre !== '' && numeroSaisiPropre !== propose.data
          ? numeroSaisiPropre
          : undefined;
      let oeuvreId: string;
      try {
        const oeuvre = await creerOeuvre.mutateAsync({
          titre,
          ...(registrePoliceActif && vendeur ? { vendeurContactId: vendeur.id } : {}),
          ...(registrePoliceActif && prixAchat !== null ? { prixAchatCentimes: prixAchat } : {}),
          artisteId: idArtiste,
          ...(numeroEnvoye === undefined ? {} : { numeroInventaire: numeroEnvoye }),
          ...(annee ? { annee: Number(annee) } : {}),
        });
        oeuvreId = oeuvre.oeuvre.id;
      } catch (error) {
        if (estApiError(error) && error.code === 'oeuvre.numero_inventaire_existe') {
          // Le numéro saisi est pris : le champ revient à la proposition, à jour.
          setNumeroTouche(false);
          void propose.refetch();
        }
        throw error;
      }

      // La fiche existe : la suite ne l'annule jamais. La première photo
      // devient la vue principale ; les variantes se font en tâche de fond.
      let echecs = 0;
      for (const [rang, fichier] of photos.entries()) {
        setEnvoi({ rang: rang + 1, total: photos.length });
        try {
          await deposerMedia.mutateAsync({
            fichier,
            rattachementType: 'artwork',
            rattachementId: oeuvreId,
          });
        } catch {
          echecs += 1;
        }
      }
      setEnvoi(null);
      if (echecs > 0) {
        setCreeeAvecProblemes(oeuvreId);
        setPhotosEnEchec(echecs);
        return;
      }
      onOpenChange(false);
      onCreee(oeuvreId);
    } catch (error) {
      formulaire.appliquer(error, ['titre', 'artisteId', 'numeroInventaire', 'annee', 'nom']);
    } finally {
      setEnCours(false);
    }
  }

  function ajouterPhotos(fichiers: FileList | null): void {
    if (!fichiers) return;
    const images = [...fichiers].filter((fichier) => fichier.type.startsWith('image/'));
    setPhotos((avant) => [...avant, ...images].slice(0, PHOTOS_MAX));
  }

  const artisteValide =
    artisteId === NOUVEL_ARTISTE ? nouvelArtisteNom.trim().length > 0 : artisteId !== '';
  const cleErreurAnnee = erreurAnnee(annee);
  const peutSoumettre = titre.trim().length > 0 && artisteValide && !cleErreurAnnee && !enCours;
  const manques = [
    titre.trim().length === 0 ? t('regie:oeuvres.nouvelleOeuvre.titreChamp') : null,
    !artisteValide
      ? artisteId === NOUVEL_ARTISTE
        ? t('regie:oeuvres.nouvelleOeuvre.artisteNouveauNomChamp')
        : t('regie:oeuvres.nouvelleOeuvre.artisteChamp')
      : null,
  ].filter((champ): champ is string => champ !== null);
  const garde = useFermetureGardee(
    titre.trim().length > 0 ||
      artisteId !== '' ||
      nouvelArtisteNom.trim().length > 0 ||
      numeroTouche ||
      annee !== '' ||
      photos.length > 0 ||
      vendeur !== null ||
      prixAchat !== null,
    onOpenChange,
  );
  const nomArtisteApercu =
    artisteId === NOUVEL_ARTISTE
      ? nouvelArtisteNom
      : (artistes.data?.artistes.find((artiste) => artiste.id === artisteId)?.nom ?? '');

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={garde.onOpenChange}
        title={t('regie:oeuvres.nouvelleOeuvre.titre')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          creeeAvecProblemes ? (
            <Button
              onClick={() => {
                onOpenChange(false);
                onCreee(creeeAvecProblemes);
              }}
            >
              {t('regie:oeuvres.nouvelleOeuvre.ouvrirFiche')}
            </Button>
          ) : (
            <>
              <Button variant="ghost" disabled={enCours} onClick={() => garde.onOpenChange(false)}>
                {t('commun:actions.annuler')}
              </Button>
              <Button onClick={() => void soumettre()} loading={enCours} disabled={!peutSoumettre}>
                {envoi
                  ? t('regie:oeuvres.nouvelleOeuvre.envoiPhotos', envoi)
                  : enCours
                    ? t('regie:oeuvres.nouvelleOeuvre.creation')
                    : t('regie:oeuvres.nouvelleOeuvre.creer')}
              </Button>
            </>
          )
        }
      >
        {/* Le cartel se compose à mesure de la saisie : ce qu'on lira au mur.
          Décoratif — les champs ci-dessous portent l'information. */}
        <div className={styles.apercu} aria-hidden="true">
          {apercus[0] ? (
            <img className={styles.apercuOeuvre} src={apercus[0]} alt="" />
          ) : (
            <span className={styles.apercuOeuvre} />
          )}
          <span className={styles.apercuCartel}>
            {nomArtisteApercu ? (
              <span className={styles.apercuArtiste}>{nomArtisteApercu}</span>
            ) : (
              <span className={styles.trait} data-largeur="court" />
            )}
            {titre.trim() ? (
              <span className={styles.apercuTitre}>{titre.trim()}</span>
            ) : (
              <span className={styles.trait} data-largeur="long" />
            )}
            <span className={styles.apercuMeta}>{[annee, numero].filter(Boolean).join(' · ')}</span>
          </span>
        </div>

        <form className={styles.formulaire} onSubmit={(event) => event.preventDefault()} noValidate>
          <fieldset className={styles.photos}>
            <legend>{t('regie:oeuvres.nouvelleOeuvre.photosChamp')}</legend>
            {apercus.length > 0 ? (
              <ul className={styles.vignettes}>
                {apercus.map((url, rang) => (
                  <li key={url} className={styles.vignette}>
                    <img src={url} alt={photos[rang]?.name ?? ''} />
                    <button
                      type="button"
                      className={styles.retirer}
                      disabled={enCours}
                      aria-label={t('regie:oeuvres.nouvelleOeuvre.photoRetirer', {
                        rang: rang + 1,
                      })}
                      onClick={() =>
                        setPhotos((avant) => avant.filter((_, indice) => indice !== rang))
                      }
                    >
                      <X size={14} aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            {photos.length < PHOTOS_MAX ? (
              <Button
                variant="secondary"
                size="sm"
                disabled={enCours}
                onClick={() => fichierRef.current?.click()}
              >
                <ImagePlus size={16} aria-hidden />
                {t('regie:oeuvres.nouvelleOeuvre.photosAjouter')}
              </Button>
            ) : null}
            {/* Sur tablette et téléphone, le sélecteur du système offre
              l'appareil photo, la photothèque et les fichiers. */}
            <input
              ref={fichierRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              aria-label={t('regie:oeuvres.nouvelleOeuvre.photosAjouter')}
              onChange={(event) => {
                ajouterPhotos(event.target.files);
                event.target.value = '';
              }}
            />
            <p className={styles.aide}>{t('regie:oeuvres.nouvelleOeuvre.photosAide')}</p>
          </fieldset>

          <Field
            label={t('regie:oeuvres.nouvelleOeuvre.titreChamp')}
            htmlFor="nouvelle-oeuvre-titre"
            error={formulaire.messageChamp('titre')}
          >
            <Input
              id="nouvelle-oeuvre-titre"
              maxLength={LIMITES.oeuvre.titre}
              required
              value={titre}
              onChange={(event) => setTitre(event.target.value)}
            />
          </Field>

          <Field
            label={t('regie:oeuvres.nouvelleOeuvre.artisteChamp')}
            htmlFor="nouvelle-oeuvre-artiste"
            error={formulaire.messageChamp('artisteId')}
          >
            <Select
              id="nouvelle-oeuvre-artiste"
              aria-label={t('regie:oeuvres.nouvelleOeuvre.artisteChamp')}
              options={optionsArtistes}
              value={artisteId}
              onValueChange={setArtisteId}
              placeholder={t('regie:oeuvres.nouvelleOeuvre.artisteChoisir')}
            />
          </Field>

          {artisteId === NOUVEL_ARTISTE ? (
            <Field
              label={t('regie:oeuvres.nouvelleOeuvre.artisteNouveauNomChamp')}
              htmlFor="nouvelle-oeuvre-artiste-nom"
              error={formulaire.messageChamp('nom')}
            >
              <Input
                id="nouvelle-oeuvre-artiste-nom"
                maxLength={LIMITES.artiste.nom}
                required
                value={nouvelArtisteNom}
                onChange={(event) => setNouvelArtisteNom(event.target.value)}
              />
            </Field>
          ) : null}

          {registrePoliceActif && (
            <fieldset>
              <legend>{t('regie:registrePolice.vendeurOeuvre')}</legend>
              <RechercheContact
                galerieSlug={galerieSlug}
                contact={vendeur}
                onChoisir={setVendeur}
                onEffacer={() => setVendeur(null)}
              />
              <Field
                htmlFor="nouvelle-oeuvre-prix-achat"
                label={t('regie:registrePolice.prixAchat')}
              >
                <PriceInput
                  id="nouvelle-oeuvre-prix-achat"
                  lang={i18n.language === 'en' ? 'en' : 'fr'}
                  optional
                  cents={prixAchat}
                  onCentsChange={setPrixAchat}
                />
              </Field>
            </fieldset>
          )}
          <div className={styles.grille2}>
            <Field
              label={t('regie:oeuvres.nouvelleOeuvre.numeroInventaireChamp')}
              htmlFor="nouvelle-oeuvre-numero"
              error={formulaire.messageChamp('numeroInventaire')}
            >
              <Input
                id="nouvelle-oeuvre-numero"
                maxLength={LIMITES.oeuvre.numeroInventaire}
                mono
                value={numero}
                onChange={(event) => {
                  setNumeroSaisi(event.target.value);
                  setNumeroTouche(true);
                }}
              />
              <p className={styles.aide}>
                {t('regie:oeuvres.nouvelleOeuvre.numeroInventaireAide')}
              </p>
            </Field>

            <Field
              label={t('regie:oeuvres.nouvelleOeuvre.anneeChamp')}
              htmlFor="nouvelle-oeuvre-annee"
              error={annee && cleErreurAnnee ? t(cleErreurAnnee) : formulaire.messageChamp('annee')}
            >
              <Input
                id="nouvelle-oeuvre-annee"
                mono
                type="number"
                inputMode="numeric"
                min={0}
                max={2999}
                step={1}
                value={annee}
                onChange={(event) => setAnnee(event.target.value)}
              />
            </Field>
          </div>

          <ChampsManquants champs={manques} />
          {photosEnEchec > 0 ? (
            <p className={styles.erreur} role="alert">
              {t('regie:oeuvres.nouvelleOeuvre.photosEchec', { count: photosEnEchec })}
            </p>
          ) : null}
          {formulaire.erreur ? (
            <p className={styles.erreur} role="alert">
              {formulaire.erreur}
            </p>
          ) : null}
        </form>
      </Dialog>
      {garde.confirmation}
    </>
  );
}
